import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminPagedQuery, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { CollectionEditor, FormActions } from '../components/CollectionEditor'
import { Field, I18nField, SelectField, type I18nValue } from '../components/fields'
import { RbacError } from '../components/RbacError'
import { useAdminContext } from '../session'
import { useCollection, type Page } from '../useCollection'
import type { I18n, PermissionEntry, Role, RoleHolder } from '../types'

/**
 * L'ordre d'affichage des modules : celui d'une soirée qu'on prépare, de
 * l'édition à l'administration. Un module inconnu (ajouté côté serveur sans
 * traduction) passe à la fin sous son nom brut, plutôt que de disparaître.
 */
const MODULES = [
  'event',
  'catalog',
  'ticketing',
  'tables',
  'pricing',
  'orders',
  'program',
  'competition',
  'voting',
  'checkin',
  'single',
  'kitchen',
  'bar',
  'service',
  'pos',
  'inventory',
  'analytics',
  'project',
  'finance',
  'admin',
] as const

/** Le motif exigé par le serveur pour un code de rôle. */
const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/

/** Les clés de cache qu'un changement de rôle rend périmées ailleurs. */
const RELATED: string[][] = [['admin-role-options'], ['admin-users'], ['admin-me']]

function localized(value: I18n | null | undefined, language: string): string {
  if (!value) return ''
  return value[language] || Object.values(value).find((entry) => entry?.trim()) || ''
}

/**
 * Les refus du serveur que l'écran sait dire dans la langue de l'interface.
 * Les autres gardent le message du serveur (et `RbacError` la liste des
 * permissions non détenues).
 */
function useRoleError() {
  const { t } = useTranslation()
  return (error: unknown): string | null => {
    if (!(error instanceof ApiError)) return null
    switch (error.code) {
      case 'ROLE_SELF_LOCKOUT':
        return t('admin.roles.errors.selfLockout')
      case 'ROLE_LOCKED':
        return t('admin.roles.errors.locked')
      case 'ROLE_SYSTEM':
        return t('admin.roles.errors.system')
      case 'ROLE_IN_USE':
        return t('admin.roles.errors.inUse')
      case 'ROLE_CODE_TAKEN':
        return t('admin.roles.errors.codeTaken')
      default:
        return null
    }
  }
}

function RoleErrorBox({
  error,
  permissions,
}: {
  error: unknown
  permissions: PermissionEntry[]
}) {
  const translate = useRoleError()
  const known = translate(error)
  if (known) {
    return (
      <p className="mt-4 rounded-md border border-state-danger/40 px-3 py-3 text-sm text-state-danger">
        {known}
      </p>
    )
  }
  return (
    <RbacError
      error={error}
      describe={(missing) => permissions.find((entry) => entry.code === missing)?.description}
    />
  )
}

/**
 * Les rôles et ce qu'ils permettent.
 *
 * Un rôle n'est qu'une liste de permissions ; l'écran sert d'abord à LIRE
 * cette liste en clair (« Encaisser à la caisse » plutôt que `pos.sale.create`),
 * ensuite à composer, modifier et supprimer des rôles.
 *
 * * Les rôles **sur mesure** se modifient et se suppriment. Un rôle encore
 *   porté ne se supprime qu'en désignant le rôle que ses porteurs reçoivent à
 *   la place : personne ne perd ses accès sans qu'on l'ait décidé.
 * * Les rôles **système** se modifient (nom, description, permissions) mais
 *   ne se suppriment pas. Un retrait est conservé lors des mises à jour.
 * * **Administrateur** est verrouillé : il garantit qu'une personne au moins
 *   peut toujours tout réparer. On peut le dupliquer.
 *
 * Avant d'enregistrer une nouvelle liste de permissions, l'écran montre ce
 * qui est ajouté et retiré : un rôle système est porté par toute une équipe.
 */
export function AdminRolesPage() {
  const { t, i18n } = useTranslation()
  const { eventId, can, me } = useAdminContext()

  useEffect(() => {
    document.title = t('admin.roles.title')
  }, [t])

  const roles = useCollection<Role>({
    key: 'admin-roles',
    path: '/admin/roles',
    eventId,
    // La composition d'un rôle change les noms proposés dans « Équipe », et
    // peut-être mes propres droits si je le porte.
    related: RELATED,
  })

  // Le catalogue entier, page après page : il compte près de deux cents
  // entrées et en comptera davantage.
  const catalog = useAdminPagedQuery<PermissionEntry>(
    ['admin-permissions', eventId],
    (page) => `/admin/permissions?page_size=200&page=${page}`,
    eventId,
  )
  const permissions = useMemo(() => catalog.data ?? [], [catalog.data])
  const byCode = useMemo(
    () => new Map(permissions.map((entry) => [entry.code, entry])),
    [permissions],
  )
  const held = useMemo(() => new Set(me?.permissions ?? []), [me?.permissions])

  const canCreate = can('admin.role.create')
  const canUpdate = can('admin.role.update')
  const canDelete = can('admin.role.delete')

  // Le rôle qu'on duplique : son formulaire s'ouvre au-dessus de la liste.
  const [duplicateOf, setDuplicateOf] = useState<Role | null>(null)
  const duplicate = canCreate
    ? (role: Role) => {
        roles.create.reset()
        setDuplicateOf(role)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    : null

  return (
    <>
      <AdminHeader title={t('admin.roles.title')} description={t('admin.roles.description')} />

      {duplicateOf ? (
        <section className="mb-6 rounded-lg border border-line-subtle bg-bg-elevated px-4 py-5 sm:px-6">
          <h2 className="font-display text-heading-md text-content-primary">
            {t('admin.roles.duplicateTitle', {
              name: localized(duplicateOf.name_i18n, i18n.language) || duplicateOf.code,
            })}
          </h2>
          <div className="mt-4">
            <RoleForm
              key={duplicateOf.id}
              role={null}
              template={duplicateOf}
              permissions={permissions}
              held={held}
              busy={roles.busy}
              error={roles.create.error}
              onCancel={() => setDuplicateOf(null)}
              onSubmit={(payload) =>
                roles.create.mutate(payload, { onSuccess: () => setDuplicateOf(null) })
              }
            />
          </div>
        </section>
      ) : null}

      <CollectionEditor<Role>
        title={t('admin.roles.list')}
        description={t('admin.roles.listHint')}
        items={roles.items}
        busy={roles.busy}
        // Seule la suppression rapide remonte ici (rôle sur mesure que
        // personne ne porte) ; les autres refus s'affichent dans le tiroir.
        error={roles.remove.error}
        emptyLabel={t('admin.roles.empty')}
        createLabel={canCreate ? t('admin.roles.create') : null}
        onCreate={() => setDuplicateOf(null)}
        summarize={(role) => ({
          title: localized(role.name_i18n, i18n.language) || role.code,
          subtitle: localized(role.description_i18n, i18n.language) || role.code,
          value: [
            t('admin.roles.permissionCount', { count: role.permissions?.length ?? 0 }),
            t('admin.roles.holderCount', { count: role.holders ?? 0 }),
          ].join(' · '),
          badges: role.locked
            ? [{ label: t('admin.roles.lockedBadge'), tone: 'warning' as const }]
            : role.is_system
              ? [{ label: t('admin.roles.systemBadge') }]
              : [{ label: t('admin.roles.customBadge'), tone: 'success' as const }],
        })}
        // La croix de la liste ne sert qu'au cas simple : un rôle sur mesure
        // que personne ne porte. Les autres se suppriment depuis le tiroir,
        // où l'on choisit à qui confier leurs porteurs.
        {...(canDelete ? { onDelete: (role: Role) => roles.remove.mutate({ id: role.id }) } : {})}
        canDelete={(role) => !role.is_system && (role.holders ?? 0) === 0}
        renderForm={(role, close) => {
          if (!role) {
            return (
              <RoleForm
                role={null}
                permissions={permissions}
                held={held}
                busy={roles.busy}
                error={roles.create.error}
                onCancel={close}
                onSubmit={(payload) => roles.create.mutate(payload, { onSuccess: close })}
              />
            )
          }
          const editable = canUpdate && !role.locked
          return (
            <div className="space-y-8">
              {editable ? (
                <RoleForm
                  role={role}
                  permissions={permissions}
                  held={held}
                  busy={roles.busy}
                  error={roles.update.error}
                  onCancel={close}
                  onSubmit={(payload) =>
                    roles.update.mutate({ id: role.id, ...payload }, { onSuccess: close })
                  }
                />
              ) : (
                <RoleDetail role={role} byCode={byCode} />
              )}

              {duplicate ? (
                <div className="border-t border-line-subtle pt-5">
                  <Button size="sm" variant="outline" onClick={() => duplicate(role)}>
                    {t('admin.roles.duplicate')}
                  </Button>
                  <p className="mt-2 text-xs text-content-muted">{t('admin.roles.duplicateHint')}</p>
                </div>
              ) : null}

              <RoleHolders role={role} eventId={eventId} />

              {canDelete ? (
                <DeleteRole
                  role={role}
                  roles={roles.items}
                  eventId={eventId}
                  permissions={permissions}
                  onDeleted={close}
                />
              ) : null}
            </div>
          )
        }}
      />

      {catalog.isError ? (
        <p className="mt-4 text-sm text-state-danger">{t('admin.roles.catalogFailed')}</p>
      ) : null}
    </>
  )
}

/** Un nom de module traduit, ou brut s'il est inconnu. */
function useModuleLabel() {
  const { t } = useTranslation()
  return (module: string) => t(`admin.roles.modules.${module}`, { defaultValue: module })
}

/** Regroupe des permissions par module, dans l'ordre de `MODULES`. */
function groupByModule<T extends { module: string }>(entries: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>()
  for (const entry of entries) {
    const list = groups.get(entry.module) ?? []
    list.push(entry)
    groups.set(entry.module, list)
  }
  const rank = (module: string) => {
    const index = (MODULES as readonly string[]).indexOf(module)
    return index === -1 ? MODULES.length : index
  }
  return [...groups.entries()].sort(
    ([left], [right]) => rank(left) - rank(right) || left.localeCompare(right),
  )
}

/** Une permission du rôle, même si le catalogue ne la connaît plus. */
function entryFor(code: string, byCode: Map<string, PermissionEntry>): PermissionEntry {
  return (
    byCode.get(code) ?? {
      id: code,
      code,
      module: code.split('.')[0] ?? code,
      description: '',
      is_sensitive: false,
    }
  )
}

function SensitiveMark() {
  const { t } = useTranslation()
  return (
    <span
      className="ml-2 inline-block rounded-full border border-state-warning/40 px-1.5 py-px text-[10px] uppercase text-state-warning"
      title={t('admin.roles.sensitiveHint')}
    >
      {t('admin.roles.sensitive')}
    </span>
  )
}

/**
 * Un rôle en lecture : ce qu'il permet, module par module.
 *
 * Une permission que le catalogue ne connaît plus reste listée sous son code :
 * la taire laisserait croire que le rôle en accorde moins qu'il n'en accorde.
 */
function RoleDetail({ role, byCode }: { role: Role; byCode: Map<string, PermissionEntry> }) {
  const { t, i18n } = useTranslation()
  const moduleLabel = useModuleLabel()
  const entries = (role.permissions ?? []).map((code) => entryFor(code, byCode))
  const description = localized(role.description_i18n, i18n.language)

  return (
    <div>
      <p className="font-mono text-xs text-content-muted">{role.code}</p>
      {description ? <p className="mt-2 text-sm text-content-secondary">{description}</p> : null}

      <p className="mt-3 rounded-md border border-line-subtle bg-bg-elevated px-3 py-2 text-xs text-content-muted">
        {role.locked ? t('admin.roles.lockedNotice') : t('admin.roles.noUpdateRight')}
      </p>

      {entries.length === 0 ? (
        <p className="mt-4 text-sm text-content-muted">{t('admin.roles.noPermissions')}</p>
      ) : (
        <div className="mt-4 space-y-4">
          {groupByModule(entries).map(([module, list]) => (
            <section key={module}>
              <h3 className="text-caption uppercase tracking-wide text-content-muted">
                {moduleLabel(module)}
              </h3>
              <ul className="mt-1.5 space-y-1">
                {list.map((entry) => (
                  <li key={entry.code} className="text-sm">
                    <span className="text-content-primary">{entry.description || entry.code}</span>
                    {entry.is_sensitive ? <SensitiveMark /> : null}
                    <span className="block break-words font-mono text-[11px] text-content-muted">
                      {entry.code}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

interface RolePayload {
  code?: string
  name_i18n: I18nValue
  description_i18n: I18nValue
  permissions?: string[]
}

/** Un code proposé pour la copie : `CODE_COPIE`, borné à 50 caractères. */
function copyCode(code: string, suffix: string): string {
  return `${code}_${suffix}`.slice(0, 50)
}

/**
 * Créer, dupliquer ou modifier un rôle.
 *
 * Les permissions que je ne détiens pas sont grisées : le serveur refuserait
 * de les AJOUTER (sinon n'importe quel gestionnaire se fabriquerait un rôle
 * plus puissant que le sien). On les montre quand même, pour qu'on sache
 * qu'elles existent et qui aller voir.
 *
 * Une permission non détenue mais DÉJÀ présente dans le rôle reste cochable :
 * la retirer ne donne rien à personne, la garder non plus.
 *
 * En modification, si la liste change, l'enregistrement passe par un
 * récapitulatif (ajouts, retraits, nombre d'attributions touchées) : un rôle
 * système est porté par toute une équipe, et le changement est immédiat.
 */
function RoleForm({
  role,
  template,
  permissions,
  held,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  role: Role | null
  /** Rôle dupliqué : son nom, sa description et ses permissions détenues. */
  template?: Role | undefined
  permissions: PermissionEntry[]
  held: Set<string>
  busy: boolean
  error: unknown
  onSubmit: (payload: RolePayload) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const moduleLabel = useModuleLabel()
  const isNew = role === null

  const [code, setCode] = useState(() =>
    template ? copyCode(template.code, t('admin.roles.copyCode')) : '',
  )
  const [name, setName] = useState<I18nValue>(() => {
    if (role) return role.name_i18n
    if (!template) return {}
    const suffix = t('admin.roles.copySuffix')
    return Object.fromEntries(
      Object.entries(template.name_i18n).map(([locale, text]) => [locale, `${text} ${suffix}`]),
    )
  })
  const [description, setDescription] = useState<I18nValue>(
    () => role?.description_i18n ?? template?.description_i18n ?? {},
  )
  const initial = useMemo(() => new Set(role?.permissions ?? []), [role?.permissions])
  // Une copie ne reprend que ce que je détiens : le reste serait refusé.
  const dropped = useMemo(
    () => (template?.permissions ?? []).filter((entry) => !held.has(entry)),
    [template?.permissions, held],
  )
  const [selected, setSelected] = useState<Set<string>>(() =>
    template
      ? new Set((template.permissions ?? []).filter((entry) => held.has(entry)))
      : new Set(initial),
  )
  const [search, setSearch] = useState('')
  const [reviewing, setReviewing] = useState(false)

  const codeValid = !isNew || (code.length >= 2 && code.length <= 50 && CODE_PATTERN.test(code))
  const hasName = Object.values(name).some((value) => value.trim())

  const byCode = useMemo(
    () => new Map(permissions.map((entry) => [entry.code, entry])),
    [permissions],
  )
  const needle = search.trim().toLowerCase()
  // Recalculé à chaque rendu : deux cents lignes filtrées ne coûtent rien,
  // et la recherche porte aussi sur le nom traduit du module.
  const visible = needle
    ? permissions.filter((entry) =>
        [entry.code, entry.description, moduleLabel(entry.module)].some((text) =>
          text.toLowerCase().includes(needle),
        ),
      )
    : permissions
  const groups = groupByModule(visible)

  const added = [...selected].filter((entry) => !initial.has(entry)).sort()
  const removed = [...initial].filter((entry) => !selected.has(entry)).sort()
  const changed = added.length > 0 || removed.length > 0
  // Droits déjà dans le rôle que je n'ai pas : je peux les retirer, pas les rajouter.
  const foreign = [...initial].filter((entry) => !held.has(entry))

  /** Cochable : détenue, ou déjà présente dans le rôle (donc retirable). */
  const selectable = (entry: PermissionEntry) => held.has(entry.code) || initial.has(entry.code)

  function toggle(codeToToggle: string) {
    setReviewing(false)
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(codeToToggle)) next.delete(codeToToggle)
      else next.add(codeToToggle)
      return next
    })
  }

  /** « Tout le module » ne touche qu'aux permissions détenues et affichées. */
  function toggleModule(list: PermissionEntry[], on: boolean) {
    setReviewing(false)
    setSelected((current) => {
      const next = new Set(current)
      for (const entry of list) {
        if (!held.has(entry.code)) continue
        if (on) next.add(entry.code)
        else next.delete(entry.code)
      }
      return next
    })
  }

  function submit() {
    onSubmit({
      ...(isNew ? { code } : {}),
      name_i18n: name,
      description_i18n: description,
      // En modification, la liste ne part que si elle a changé : renommer un
      // rôle ne doit pas réécrire ses permissions.
      ...(isNew || changed ? { permissions: [...selected].sort() } : {}),
    })
  }

  const holders = role?.holders ?? 0

  return (
    <div>
      <div className="grid gap-5">
        {isNew ? (
          <Field label={t('admin.roles.code')} hint={t('admin.roles.codeHint')}>
            <input
              value={code}
              // Saisie forcée en majuscules, espaces changés en « _ » : le
              // motif serveur est strict, et un refus après coup pour une
              // minuscule serait une perte de temps.
              onChange={(changeEvent) =>
                setCode(changeEvent.target.value.toUpperCase().replace(/[\s-]+/g, '_'))
              }
              maxLength={50}
              autoCapitalize="characters"
              className={cn(
                'w-full rounded-md border bg-bg-base px-3 py-2.5 font-mono text-content-primary',
                code && !codeValid ? 'border-state-danger/60' : 'border-line',
              )}
            />
          </Field>
        ) : (
          <p className="font-mono text-xs text-content-muted">{role.code}</p>
        )}
        {role?.is_system ? (
          <p className="rounded-md border border-line-subtle bg-bg-elevated px-3 py-2 text-xs text-content-muted">
            {t('admin.roles.systemEditNotice')}
          </p>
        ) : null}
        <I18nField label={t('admin.roles.name')} value={name} onChange={setName} />
        <I18nField
          label={t('admin.roles.roleDescription')}
          value={description}
          onChange={setDescription}
          multiline
        />
      </div>

      <section className="mt-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.roles.permissions')}
          </p>
          <p className="text-xs text-content-muted">
            {t('admin.roles.selectedCount', { count: selected.size })}
          </p>
        </div>
        <p className="mt-1 text-xs text-content-muted">{t('admin.roles.notHeldHint')}</p>

        {foreign.length && !isNew ? (
          <p className="mt-2 rounded-md border border-state-warning/40 px-3 py-2 text-xs text-state-warning">
            {t('admin.roles.foreignWarning', { count: foreign.length })}
          </p>
        ) : null}
        {dropped.length ? (
          <p className="mt-2 rounded-md border border-state-warning/40 px-3 py-2 text-xs text-state-warning">
            {t('admin.roles.duplicateDropped', { count: dropped.length })}
          </p>
        ) : null}

        <label className="mt-3 block">
          <span className="sr-only">{t('admin.roles.search')}</span>
          <input
            type="search"
            value={search}
            onChange={(changeEvent) => setSearch(changeEvent.target.value)}
            placeholder={t('admin.roles.search')}
            className="w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary"
          />
        </label>

        <div className="mt-3 space-y-3">
          {groups.length === 0 ? (
            <p className="text-sm text-content-muted">{t('admin.roles.searchEmpty')}</p>
          ) : null}
          {groups.map(([module, list]) => {
            const ownable = list.filter((entry) => held.has(entry.code))
            const allOn = ownable.length > 0 && ownable.every((entry) => selected.has(entry.code))
            const someOn = list.some((entry) => selected.has(entry.code))
            return (
              <fieldset key={module} className="min-w-0 rounded-md border border-line-subtle px-3 py-3">
                <legend className="px-1 text-sm text-content-primary">{moduleLabel(module)}</legend>
                <label
                  className={cn(
                    'flex items-center gap-2 text-xs text-content-secondary',
                    ownable.length === 0 && 'opacity-50',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={allOn}
                    disabled={ownable.length === 0}
                    ref={(element) => {
                      // L'état « en partie » ne s'exprime qu'en propriété DOM.
                      if (element) element.indeterminate = someOn && !allOn
                    }}
                    onChange={() => toggleModule(list, !allOn)}
                    className="h-4 w-4 shrink-0"
                  />
                  {needle ? t('admin.roles.wholeModuleShown') : t('admin.roles.wholeModule')}
                </label>

                <ul className="mt-2 space-y-2">
                  {list.map((entry) => {
                    const enabled = selectable(entry)
                    return (
                      <li key={entry.code}>
                        <label
                          className={cn(
                            'flex items-start gap-2 text-sm',
                            enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(entry.code)}
                            disabled={!enabled}
                            onChange={() => toggle(entry.code)}
                            className="mt-0.5 h-4 w-4 shrink-0"
                          />
                          <span className="min-w-0">
                            <span className="text-content-primary">
                              {entry.description || entry.code}
                            </span>
                            {entry.is_sensitive ? <SensitiveMark /> : null}
                            <span className="block break-words font-mono text-[11px] text-content-muted">
                              {entry.code}
                            </span>
                            {held.has(entry.code) ? null : (
                              <span className="block text-[11px] text-content-muted">
                                {t('admin.roles.notHeld')}
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              </fieldset>
            )
          })}
        </div>
      </section>

      <RoleErrorBox error={error} permissions={permissions} />

      {reviewing && changed && !isNew ? (
        <section className="mt-5 rounded-md border border-state-warning/40 px-3 py-3">
          <h3 className="text-sm text-content-primary">{t('admin.roles.reviewTitle')}</h3>
          <DiffList
            label={t('admin.roles.added', { count: added.length })}
            codes={added}
            byCode={byCode}
            tone="add"
          />
          <DiffList
            label={t('admin.roles.removed', { count: removed.length })}
            codes={removed}
            byCode={byCode}
            tone="remove"
          />
          <p className="mt-3 text-xs text-state-warning">
            {holders
              ? t('admin.roles.reviewHolders', { count: holders })
              : t('admin.roles.reviewNoHolders')}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={busy} onClick={submit}>
              {busy ? t('state.loading') : t('admin.roles.confirmSave')}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setReviewing(false)}>
              {t('admin.roles.back')}
            </Button>
          </div>
        </section>
      ) : (
        <FormActions
          busy={busy || !codeValid || !hasName}
          onCancel={onCancel}
          // Une liste modifiée passe d'abord par le récapitulatif.
          onSubmit={() => (!isNew && changed ? setReviewing(true) : submit())}
          {...(!isNew && changed ? { submitLabel: t('admin.roles.review') } : {})}
        />
      )}
    </div>
  )
}

function DiffList({
  label,
  codes,
  byCode,
  tone,
}: {
  label: string
  codes: string[]
  byCode: Map<string, PermissionEntry>
  tone: 'add' | 'remove'
}) {
  if (codes.length === 0) return null
  return (
    <div className="mt-3">
      <p
        className={cn(
          'text-caption uppercase tracking-wide',
          tone === 'add' ? 'text-state-success' : 'text-state-danger',
        )}
      >
        {label}
      </p>
      <ul className="mt-1 space-y-1">
        {codes.map((code) => {
          const entry = entryFor(code, byCode)
          return (
            <li key={code} className="text-sm">
              <span className="text-content-primary">
                {tone === 'add' ? '+ ' : '− '}
                {entry.description || entry.code}
              </span>
              {entry.is_sensitive ? <SensitiveMark /> : null}
              <span className="block break-words font-mono text-[11px] text-content-muted">
                {entry.code}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Les attributions d'un rôle : qui le porte, et sur quelle portée. */
function useHolders(role: Role, eventId: string | undefined) {
  // Hors du préfixe `admin-roles` : supprimer le rôle invalide la liste, et
  // une relecture de ses porteurs répondrait 404 pendant que le tiroir se ferme.
  // Le tiroir relit de toute façon à chaque ouverture.
  return useAdminQuery<Page<RoleHolder>>(
    ['admin-role-holders', role.id, role.holders ?? 0],
    `/admin/roles/${role.id}/holders?page_size=200`,
    eventId,
    { enabled: (role.holders ?? 0) > 0 },
  )
}

function RoleHolders({ role, eventId }: { role: Role; eventId: string | undefined }) {
  const { t, i18n } = useTranslation()
  const query = useHolders(role, eventId)
  const count = role.holders ?? 0
  const rows = query.data?.items ?? []

  return (
    <section className="border-t border-line-subtle pt-5">
      <h3 className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.roles.holdersTitle')}
      </h3>
      {count === 0 ? (
        <p className="mt-2 text-sm text-content-muted">{t('admin.roles.holdersEmpty')}</p>
      ) : query.isPending ? (
        <p className="mt-2 text-sm text-content-muted">{t('state.loading')}</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {rows.map((row) => (
            <li key={row.user_role_id} className="min-w-0 text-sm">
              <span className={cn('text-content-primary', !row.is_active && 'opacity-60')}>
                {row.full_name}
              </span>
              {row.is_active ? null : (
                <span className="ml-2 text-[11px] uppercase text-content-muted">
                  {t('admin.roles.inactive')}
                </span>
              )}
              <span className="block break-all text-xs text-content-muted">{row.email}</span>
              <span className="block text-xs text-content-muted">
                {row.event_id
                  ? [localized(row.event_name_i18n, i18n.language), row.event_year]
                      .filter(Boolean)
                      .join(' · ')
                  : t('admin.roles.scopeGlobal')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Supprimer un rôle depuis son tiroir.
 *
 * Un rôle système ne se supprime pas : on dit pourquoi, et quoi faire à la
 * place. Un rôle sur mesure encore porté exige un remplaçant — chaque
 * attribution passe au rôle choisi, sur la même portée — plutôt que de
 * retirer des accès en silence. Administrateur n'est jamais proposé comme
 * remplaçant : il se donne personne par personne, dans Équipe.
 */
function DeleteRole({
  role,
  roles,
  eventId,
  permissions,
  onDeleted,
}: {
  role: Role
  roles: Role[]
  eventId: string | undefined
  permissions: PermissionEntry[]
  onDeleted: () => void
}) {
  const { t, i18n } = useTranslation()
  const [replacement, setReplacement] = useState('')
  const [confirming, setConfirming] = useState(false)
  const remove = useAdminMutation<{ message: string }, { replacement: string }>(
    'delete',
    ({ replacement: target }) =>
      `/admin/roles/${role.id}${target ? `?replacement_role_id=${target}` : ''}`,
    eventId,
    [['admin-roles'], ...RELATED],
  )

  const name = localized(role.name_i18n, i18n.language) || role.code
  const holders = role.holders ?? 0

  if (role.is_system) {
    return (
      <section className="border-t border-line-subtle pt-5">
        <h3 className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.roles.deleteTitle')}
        </h3>
        <p className="mt-2 text-xs text-content-muted">
          {role.locked ? t('admin.roles.deleteLocked') : t('admin.roles.deleteSystem')}
        </p>
      </section>
    )
  }

  const choices = roles.filter((entry) => entry.id !== role.id && !entry.locked)
  const target = choices.find((entry) => entry.id === replacement)
  const ready = holders === 0 || Boolean(target)

  return (
    <section className="border-t border-line-subtle pt-5">
      <h3 className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.roles.deleteTitle')}
      </h3>
      {holders === 0 ? (
        <p className="mt-2 text-xs text-content-muted">{t('admin.roles.deleteUnused')}</p>
      ) : (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-state-warning">
            {t('admin.roles.deleteInUse', { count: holders })}
          </p>
          <SelectField
            label={t('admin.roles.replacement')}
            hint={t('admin.roles.replacementHint')}
            value={replacement}
            onChange={(value) => {
              setConfirming(false)
              setReplacement(value)
            }}
            options={[
              { value: '', label: t('admin.roles.replacementPick') },
              ...choices.map((entry) => ({
                value: entry.id,
                label: localized(entry.name_i18n, i18n.language) || entry.code,
              })),
            ]}
          />
        </div>
      )}

      {confirming && ready ? (
        <div className="mt-3 space-y-2">
          <p className="text-sm text-state-danger">
            {target
              ? t('admin.roles.deleteConfirmReplace', {
                  count: holders,
                  name,
                  replacement: localized(target.name_i18n, i18n.language) || target.code,
                })
              : t('admin.roles.deleteConfirmPlain', { name })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(
                  { replacement: target ? target.id : '' },
                  { onSuccess: onDeleted },
                )
              }
            >
              {remove.isPending ? t('state.loading') : t('admin.roles.deleteConfirm')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
              {t('admin.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="mt-3"
          disabled={!ready}
          onClick={() => {
            remove.reset()
            setConfirming(true)
          }}
        >
          {t('admin.roles.deleteButton')}
        </Button>
      )}

      <RoleErrorBox error={remove.error} permissions={permissions} />
    </section>
  )
}
